import React from 'react'

import { useDashboardFeatureFlags } from '@components'
import { NavTabs } from '@components/common/NavTabs'
import {
  Banner,
  Button,
  type ButtonProps,
  HeadContent,
  List,
  type ListProps,
  Table,
  type TableProps,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import type { AvailableRole } from '@hooks/useAvailableRoles'

import {
  AddSubscriptionModal,
  UpgradeSubscriptionBanner,
  useSubscription,
  useTopSubscriptionPlan,
} from '../../../../modules/Subscriptions'
import * as S from './Team.styled'
import type { TeamMemberItem } from './Team.types'
import { RoleListItem } from './components/RoleListItem/RoleListItem'
import type { TeamMemberDropdownProps } from './components/TeamMemberDropdown/TeamMemberDropdown'
import { TeamMemberListItem } from './components/TeamMemberListItem/TeamMemberListItem'

type TeamViewProps = {
  tabsProps: {
    tabs: { label: string; value: string }[]
    activeTab: string
    onTabClick: (tab: string) => void
  }
  teamTab: {
    listProps: Omit<ListProps, 'renderItem'>
    tableProps: TableProps
    buttonProps?: Partial<ButtonProps>
    dropdownProps: Omit<TeamMemberDropdownProps, 'teamMember'>
  }
  rolesTab: {
    listProps: Omit<ListProps, 'renderItem'>
    tableProps: TableProps
  }
}

export const TeamView = ({ tabsProps, teamTab, rolesTab }: TeamViewProps) => {
  const media = useMediaQueryContext()
  const { open } = useModalContext()
  const { canUpgradeSubscription, teamMembers, subscriptionData } = useSubscription()
  const { getTopSubscriptionPlanLimitLabel } = useTopSubscriptionPlan()
  const topSubscriptionPlanLimitLabel = getTopSubscriptionPlanLimitLabel('members')
  const teamMembersLimitLabel =
    topSubscriptionPlanLimitLabel === 'unlimited'
      ? `${topSubscriptionPlanLimitLabel} amount of`
      : topSubscriptionPlanLimitLabel
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const handleUpgradeSubscription = () => {
    open(AddSubscriptionModal, {
      title: `Upgrade your plan to add more than ${subscriptionData?.members.limit} team members`,
      source: 'settings_team',
    })
  }

  const renderSubscriptionBanner = () => {
    if (!teamMembers.hasLimit || !subscriptionData || !dashboardFeatureFlags.subscriptions.enabled)
      return null

    // Usage < Limit - Threshold
    if (subscriptionData.members.usage < subscriptionData.members.limit - teamMembers.threshold) {
      return null
    }

    // Limit - Threshold < Usage < Limit
    if (
      subscriptionData.members.usage >= subscriptionData.members.limit - teamMembers.threshold &&
      teamMembers.canCreate
    ) {
      if (!canUpgradeSubscription) {
        return (
          <S.SubscriptionBannerContainer>
            <Banner appearance="pro">
              You've invited {subscriptionData.members.usage} out of{' '}
              {subscriptionData.members.limit} team members allowed on your current plan. To stay
              within the limit, consider removing inactive members - or{' '}
              <a href="mailto:support@fourthwall.com">contact our support team</a>.
            </Banner>
          </S.SubscriptionBannerContainer>
        )
      }

      return (
        <S.SubscriptionBannerContainer>
          <UpgradeSubscriptionBanner source="settings_team" onClick={handleUpgradeSubscription}>
            You’ve created {subscriptionData?.members.usage} out of your{' '}
            {subscriptionData?.members.limit}-member limit. Upgrade to{' '}
            <strong>Fourthwall Pro</strong> to add {teamMembersLimitLabel} members to your shop.
          </UpgradeSubscriptionBanner>
        </S.SubscriptionBannerContainer>
      )
    }

    // Usage >= Limit
    if (!teamMembers.canCreate) {
      if (!canUpgradeSubscription) {
        return (
          <S.SubscriptionBannerContainer>
            <Banner appearance="pro">
              You've invited {subscriptionData.members.usage} out of{' '}
              {subscriptionData.members.limit} team members allowed on your current plan. To invite
              someone new, please remove an existing member{' '}
              <a href="mailto:support@fourthwall.com">contact our support team</a>.
            </Banner>
          </S.SubscriptionBannerContainer>
        )
      }

      return (
        <S.SubscriptionBannerContainer>
          <UpgradeSubscriptionBanner source="settings_team" onClick={handleUpgradeSubscription}>
            You’ve hit your {subscriptionData?.members.limit}-member limit. Upgrade to{' '}
            <strong>Fourthwall Pro</strong> to add {teamMembersLimitLabel} members to your shop.
          </UpgradeSubscriptionBanner>
        </S.SubscriptionBannerContainer>
      )
    }

    return null
  }

  const renderButton = () => {
    if (tabsProps.activeTab === 'team' && teamTab.buttonProps) {
      return <Button size="small" appearance="primary" {...teamTab.buttonProps} />
    }

    return null
  }

  const renderContent = () => {
    if (tabsProps.activeTab === 'team') {
      if (!media.tablet) {
        return (
          <List
            renderItem={(item: TeamMemberItem) => (
              <TeamMemberListItem teamMember={item} dropdownProps={teamTab.dropdownProps} />
            )}
            {...teamTab.listProps}
          />
        )
      }

      return <Table minWidth={0} {...teamTab.tableProps} />
    }

    if (tabsProps.activeTab === 'roles') {
      if (!media.tablet) {
        return (
          <List
            renderItem={(role: AvailableRole) => <RoleListItem role={role} />}
            {...rolesTab.listProps}
          />
        )
      }

      return <Table minWidth={0} {...rolesTab.tableProps} />
    }

    return null
  }

  return (
    <>
      <HeadContent title="Team &ndash; Settings" />
      <S.TabsContainer>
        <NavTabs {...tabsProps} />
        {renderButton()}
      </S.TabsContainer>
      {renderSubscriptionBanner()}
      {renderContent()}
    </>
  )
}
